import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Users, Heart, BookOpen, Calendar, Sun, Shield, CheckCircle, Mail, Loader2, Send, Star } from 'lucide-react';
import { toast } from 'sonner';
import ScriptureHeader from '../components/ScriptureHeader';

const PROGRAMS = [
  {
    name: 'Sunday School',
    icon: BookOpen,
    ages: 'Ages 3-12',
    description: 'Bible stories, worship, and activities for ages 3–12',
    color: '#f59e0b'
  },
  {
    name: 'Youth Group',
    icon: Users,
    ages: 'Ages 13-17',
    description: 'Faith, friendship, and real conversations for teens 13–17',
    color: '#8b5cf6'
  },
  {
    name: 'Vacation Bible School',
    icon: Sun,
    ages: 'Summer Program',
    description: 'Annual summer program (dates announced each spring)',
    color: '#22c55e'
  },
  {
    name: 'Family Events',
    icon: Calendar,
    ages: 'All Ages',
    description: 'Seasonal celebrations for the whole family',
    color: '#ec4899'
  }
];

export default function KidsYouthPage() {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    parentName: '',
    email: '',
    phone: '',
    childName: '',
    childAge: '',
    program: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      // Store registration interest locally
      const registrations = JSON.parse(localStorage.getItem('lfm_kids_registrations') || '[]');
      registrations.push({
        id: Date.now(),
        ...formData,
        createdAt: new Date().toISOString()
      });
      localStorage.setItem('lfm_kids_registrations', JSON.stringify(registrations));
      
      setSubmitted(true);
      toast.success('Registration submitted!');
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream pt-20" data-testid="kids-youth-page">
      {/* Scripture Header */}
      <ScriptureHeader page="kids" />

      {/* Hero Section */}
      <section className="py-16 px-6 bg-gradient-to-b from-purple-50 to-cream">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="w-20 h-20 bg-purple rounded-full flex items-center justify-center mx-auto mb-6">
              <Star size={40} className="text-white" />
            </div>
            <h1 className="font-serif text-4xl md:text-5xl mb-4" style={{ color: '#1A1A1A' }}>
              Living Fruit Children's Ministry
            </h1>
            <p className="text-xl md:text-2xl italic" style={{ color: '#1E3A5F' }}>
              Where little ones grow deep roots in faith.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Subheadline */}
      <section className="py-12 px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-lg leading-relaxed" style={{ color: '#1E3A5F' }}>
              A safe, joyful, Spirit-filled space for children of all ages.
            </p>
            <div className="mt-8 p-6 bg-cream rounded-2xl">
              <p className="text-lg leading-relaxed" style={{ color: '#1E3A5F' }}>
                At Living Fruit Children's Ministry, we believe every child is a seed planted by God — 
                full of potential, purpose, and promise. Our programs are designed to nurture faith in a 
                fun, age-appropriate, and loving environment where kids can truly belong.
              </p>
              <p className="text-lg leading-relaxed mt-4" style={{ color: '#1E3A5F' }}>
                Whether your child is hearing about Jesus for the first time or growing in a faith they 
                already love, there is a place for them here.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-serif text-3xl mb-4" style={{ color: '#1A1A1A' }}>
              What We Offer
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {PROGRAMS.map((program, index) => {
              const Icon = program.icon;
              return (
                <motion.div
                  key={program.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-2xl p-6 shadow-lg border border-gold/10 text-center hover:shadow-xl transition-shadow"
                >
                  <div 
                    className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                    style={{ backgroundColor: `${program.color}20` }}
                  >
                    <Icon size={32} style={{ color: program.color }} />
                  </div>
                  <span className="text-xs uppercase tracking-wider font-medium" style={{ color: program.color }}>
                    {program.ages}
                  </span>
                  <h3 className="font-serif text-xl mt-2 mb-3" style={{ color: '#1A1A1A' }}>
                    {program.name}
                  </h3>
                  <p className="text-sm" style={{ color: '#1E3A5F' }}>
                    {program.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Safety & Care */}
      <section className="py-16 px-6 bg-gradient-to-b from-white to-green-50">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border-2 border-green-200"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center">
                <Shield size={28} className="text-green-600" />
              </div>
              <h2 className="font-serif text-2xl" style={{ color: '#1A1A1A' }}>
                Safety & Care
              </h2>
            </div>
            
            <p className="text-lg mb-6" style={{ color: '#1E3A5F' }}>
              The safety of every child is our highest priority. All of our children's ministry 
              volunteers are background-checked and trained. Parents are always welcome to observe.
            </p>

            <div className="grid md:grid-cols-2 gap-4">
              {[
                'Background-checked volunteers',
                'Safe check-in/check-out system',
                'Trained staff and leaders',
                'Parents welcome to observe'
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-3">
                  <CheckCircle className="text-green-600 flex-shrink-0" size={20} />
                  <span style={{ color: '#1A1A1A' }}>{item}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Get Involved */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="font-serif text-3xl mb-4" style={{ color: '#1A1A1A' }}>
              Get Involved
            </h2>
            <p className="text-lg mb-8" style={{ color: '#1E3A5F' }}>
              Want your child to join us? Fill out our interest form below and we will be in touch within 48 hours.
            </p>
            
            {!showForm ? (
              <button
                onClick={() => setShowForm(true)}
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-purple text-white font-semibold transition-transform hover:scale-105"
                data-testid="register-child-btn"
              >
                <Heart size={20} />
                Register Your Child
              </button>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-3xl p-8 shadow-xl border border-gold/10 text-left max-w-2xl mx-auto"
              >
                {submitted ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="text-green-600" size={32} />
                    </div>
                    <h3 className="font-serif text-2xl mb-2" style={{ color: '#1A1A1A' }}>
                      Thank You!
                    </h3>
                    <p className="text-charcoal/70 mb-6">
                      We've received your registration interest. Our children's ministry team will contact you within 48 hours.
                    </p>
                    <button
                      onClick={() => {
                        setShowForm(false);
                        setSubmitted(false);
                        setFormData({
                          parentName: '', email: '', phone: '', childName: '', childAge: '', program: '', message: ''
                        });
                      }}
                      className="px-6 py-2 rounded-full bg-purple text-white"
                    >
                      Close
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4" data-testid="kids-registration-form">
                    <h3 className="font-serif text-xl text-center mb-6" style={{ color: '#1A1A1A' }}>
                      Children's Ministry Interest Form
                    </h3>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1" style={{ color: '#1A1A1A' }}>
                          Parent/Guardian Name *
                        </label>
                        <input
                          type="text"
                          required
                          maxLength={100}
                          value={formData.parentName}
                          onChange={(e) => setFormData({ ...formData, parentName: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl border border-gold/20 focus:outline-none focus:ring-2 focus:ring-purple/50"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1" style={{ color: '#1A1A1A' }}>
                          Email *
                        </label>
                        <input
                          type="email"
                          required
                          maxLength={254}
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl border border-gold/20 focus:outline-none focus:ring-2 focus:ring-purple/50"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1" style={{ color: '#1A1A1A' }}>
                          Phone *
                        </label>
                        <input
                          type="tel"
                          required
                          maxLength={20}
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl border border-gold/20 focus:outline-none focus:ring-2 focus:ring-purple/50"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1" style={{ color: '#1A1A1A' }}>
                          Child's Name *
                        </label>
                        <input
                          type="text"
                          required
                          maxLength={100}
                          value={formData.childName}
                          onChange={(e) => setFormData({ ...formData, childName: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl border border-gold/20 focus:outline-none focus:ring-2 focus:ring-purple/50"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1" style={{ color: '#1A1A1A' }}>
                          Child's Age *
                        </label>
                        <input
                          type="text"
                          required
                          maxLength={10}
                          placeholder="e.g., 8"
                          value={formData.childAge}
                          onChange={(e) => setFormData({ ...formData, childAge: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl border border-gold/20 focus:outline-none focus:ring-2 focus:ring-purple/50"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1" style={{ color: '#1A1A1A' }}>
                          Program Interest
                        </label>
                        <select
                          value={formData.program}
                          onChange={(e) => setFormData({ ...formData, program: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl border border-gold/20 focus:outline-none focus:ring-2 focus:ring-purple/50"
                        >
                          <option value="">Select...</option>
                          <option value="sunday-school">Sunday School (Ages 3-12)</option>
                          <option value="youth-group">Youth Group (Ages 13-17)</option>
                          <option value="vbs">Vacation Bible School</option>
                          <option value="all">All Programs</option>
                        </select>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-1" style={{ color: '#1A1A1A' }}>
                        Additional Information
                      </label>
                      <textarea
                        rows={3}
                        maxLength={500}
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        placeholder="Any allergies, special needs, or questions?"
                        className="w-full px-4 py-3 rounded-xl border border-gold/20 focus:outline-none focus:ring-2 focus:ring-purple/50 resize-none"
                      />
                    </div>
                    
                    <div className="flex gap-4 pt-4">
                      <button
                        type="button"
                        onClick={() => setShowForm(false)}
                        className="px-6 py-3 rounded-full border border-charcoal/20 text-charcoal/70 hover:bg-cream transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={submitting}
                        className="flex-1 py-3 rounded-full bg-purple text-white font-semibold transition-colors hover:bg-purple/90 disabled:opacity-50 flex items-center justify-center gap-2"
                        data-testid="submit-kids-form"
                      >
                        {submitting ? (
                          <>
                            <Loader2 size={18} className="animate-spin" />
                            Submitting...
                          </>
                        ) : (
                          <>
                            <Send size={18} />
                            Submit Registration
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                )}
              </motion.div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 px-6" style={{ backgroundColor: '#5D4E6D' }}>
        <div className="max-w-3xl mx-auto text-center text-white">
          <h2 className="font-serif text-3xl mb-4">Questions?</h2>
          <p className="text-white/80 mb-6">
            Contact our Children's Ministry team for more information
          </p>
          <a
            href="mailto:livingfruitministry@gmail.com"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-white font-semibold transition-transform hover:scale-105"
            style={{ color: '#5D4E6D' }}
          >
            <Mail size={20} />
            livingfruitministry@gmail.com
          </a>
        </div>
      </section>
    </div>
  );
}
